import CompanyAPI from '../../../api/companies';
import types from '../../mutation-types';

const responsePayload = response => response.data?.payload ?? response.data;
const responseMeta = response => response.data.meta;

export const actions = {
  get: async ({ commit }, { page = 1, sortAttr = 'name' } = {}) => {
    commit(types.SET_COMPANY_UI_FLAG, { isFetching: true });
    try {
      const response = await CompanyAPI.get(page, sortAttr);
      commit(types.CLEAR_COMPANIES);
      commit(types.SET_COMPANIES, responsePayload(response));
      commit(types.SET_COMPANY_META, responseMeta(response));
    } catch (error) {
      // Keep the current table state when a transient list request fails.
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isFetching: false });
    }
  },

  search: async ({ commit }, { search, page = 1, sortAttr = 'name' } = {}) => {
    commit(types.SET_COMPANY_UI_FLAG, { isFetching: true });
    try {
      const response = await CompanyAPI.search(search, page, sortAttr);
      commit(types.CLEAR_COMPANIES);
      commit(types.SET_COMPANIES, responsePayload(response));
      commit(types.SET_COMPANY_META, responseMeta(response));
    } catch (error) {
      // Keep the current table state when a transient search request fails.
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isFetching: false });
    }
  },

  filter: async (
    { commit },
    { page = 1, sortAttr = 'name', queryPayload, resetState = true } = {}
  ) => {
    commit(types.SET_COMPANY_UI_FLAG, { isFetching: true });
    try {
      const response = await CompanyAPI.filter(page, sortAttr, queryPayload);
      if (resetState) {
        commit(types.CLEAR_COMPANIES);
        commit(types.SET_COMPANIES, responsePayload(response));
        commit(types.SET_COMPANY_META, responseMeta(response));
      }
      return responsePayload(response);
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isFetching: false });
    }
  },

  filterCount: async (_, queryPayload) => {
    const response = await CompanyAPI.filterCount(queryPayload);
    return response.data?.count || 0;
  },

  importCompanies: async ({ commit }, payload) => {
    commit(types.SET_COMPANY_UI_FLAG, { isImporting: true });
    try {
      return await CompanyAPI.importCompanies(payload);
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isImporting: false });
    }
  },

  exportCompanies: async ({ commit }, payload) => {
    commit(types.SET_COMPANY_UI_FLAG, { isExporting: true });
    try {
      return await CompanyAPI.exportCompanies(payload);
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isExporting: false });
    }
  },

  bulkAssign: async ({ commit }, payload) => {
    commit(types.SET_COMPANY_UI_FLAG, { isBulkAssigning: true });
    try {
      const { localChanges, ...apiPayload } = payload;
      const response = await CompanyAPI.bulkAssign(apiPayload);
      if (localChanges) {
        commit(types.APPLY_COMPANY_BULK_CHANGES, {
          companyIds: apiPayload.company_ids,
          changes: localChanges,
        });
      }
      return response;
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isBulkAssigning: false });
    }
  },

  bulkUpdate: async ({ commit }, payload) => {
    commit(types.SET_COMPANY_UI_FLAG, { isBulkUpdating: true });
    try {
      const { localChanges, ...apiPayload } = payload;
      const response = await CompanyAPI.bulkUpdate(apiPayload);
      if (localChanges) {
        commit(types.APPLY_COMPANY_BULK_CHANGES, {
          companyIds: apiPayload.company_ids,
          changes: localChanges,
        });
      }
      return response;
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isBulkUpdating: false });
    }
  },

  deleteSelectedCompanies: async ({ commit }, payload) => {
    commit(types.SET_COMPANY_UI_FLAG, { isDeleting: true });
    try {
      const response = await CompanyAPI.deleteSelectedCompanies(payload);
      (payload.company_ids || []).forEach(id =>
        commit(types.DELETE_COMPANY, id)
      );
      commit(types.CLEAR_SELECTED_COMPANY_IDS);
      return response;
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isDeleting: false });
    }
  },

  getWallet: async (
    { commit },
    { page = 1, sortAttr = 'name', scope = 'mine' } = {}
  ) => {
    commit(types.SET_COMPANY_UI_FLAG, { isFetchingWallet: true });
    try {
      const response = await CompanyAPI.getWallet(page, sortAttr, scope);
      commit(types.CLEAR_COMPANY_WALLET);
      commit(types.SET_COMPANY_WALLET, responsePayload(response));
      commit(types.SET_COMPANY_WALLET_META, responseMeta(response));
    } catch (error) {
      // Keep the current wallet state when a transient list request fails.
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isFetchingWallet: false });
    }
  },

  show: async ({ commit }, { id }) => {
    commit(types.SET_COMPANY_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await CompanyAPI.show(id);
      commit(types.SET_COMPANY_ITEM, responsePayload(response));
      commit(types.SET_CURRENT_COMPANY, responsePayload(response));
    } catch (error) {
      // Leave the previous profile state intact on fetch failure.
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isFetchingItem: false });
    }
  },

  fetchCompany: async ({ commit }, { id }) => {
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
      isFetchingProfile: true,
      fetchError: '',
    });
    try {
      const response = await CompanyAPI.show(id);
      const company = responsePayload(response);
      commit(types.SET_COMPANY_ITEM, company);
      commit(types.SET_CURRENT_COMPANY, company);
      return company;
    } catch (error) {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
        fetchError:
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message,
      });
      throw error;
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
        isFetchingProfile: false,
      });
    }
  },

  create: async ({ commit }, companyParams) => {
    commit(types.SET_COMPANY_UI_FLAG, { isCreating: true });
    try {
      const response = await CompanyAPI.create(companyParams);
      commit(types.SET_COMPANY_ITEM, responsePayload(response));
      return responsePayload(response);
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...companyParams }) => {
    commit(types.SET_COMPANY_UI_FLAG, { isUpdating: true });
    try {
      const response = await CompanyAPI.update(id, companyParams);
      commit(types.EDIT_COMPANY, responsePayload(response));
      return responsePayload(response);
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isUpdating: false });
    }
  },

  updateCompany: async (context, payload) => {
    return actions.update(context, payload);
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_COMPANY_UI_FLAG, { isDeleting: true });
    try {
      await CompanyAPI.delete(id);
      commit(types.DELETE_COMPANY, id);
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isDeleting: false });
    }
  },

  deleteCustomAttributes: async ({ commit }, { id, customAttributes }) => {
    const response = await CompanyAPI.destroyCustomAttributes(
      id,
      customAttributes
    );
    commit(types.EDIT_COMPANY, responsePayload(response));
    return responsePayload(response);
  },

  fetchCompanyActivityEvents: async (
    { commit },
    { companyId, page = 1, perPage = 10 } = {}
  ) => {
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
      isFetchingActivityEvents: true,
    });
    try {
      const response = await CompanyAPI.getActivityEvents(companyId, {
        page,
        per_page: perPage,
      });
      commit(types.SET_COMPANY_ACTIVITY_EVENTS, {
        data: response.data,
        page,
      });
      return responsePayload(response);
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
        isFetchingActivityEvents: false,
      });
    }
  },

  deleteAvatar: async ({ commit }, id) => {
    commit(types.SET_COMPANY_UI_FLAG, { isUpdating: true });
    try {
      const response = await CompanyAPI.destroyAvatar(id);
      commit(types.EDIT_COMPANY, responsePayload(response));
      return responsePayload(response);
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isUpdating: false });
    }
  },

  getContacts: async (_, payload) => {
    const {
      companyId,
      id = companyId,
      ...params
    } = typeof payload === 'object' ? payload : { companyId: payload };
    const response = await CompanyAPI.getContacts(id, params);
    return responsePayload(response);
  },

  getRelatedCompanies: async (_, payload) => {
    const {
      companyId,
      id = companyId,
      ...params
    } = typeof payload === 'object' ? payload : { companyId: payload };
    const response = await CompanyAPI.getRelatedCompanies(id, params);
    return responsePayload(response);
  },

  fetchCompanyContacts: async ({ commit }, payload) => {
    const {
      companyId,
      id = companyId,
      ...params
    } = typeof payload === 'object' ? payload : { companyId: payload };
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isFetchingContacts: true });
    try {
      const response = await CompanyAPI.getContacts(id, params);
      const contacts = responsePayload(response);
      commit(types.SET_COMPANY_ASSOCIATIONS, {
        type: 'contacts',
        data: contacts,
      });
      return contacts;
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isFetchingContacts: false });
    }
  },

  addCompanyContact: async ({ commit }, { companyId, ...params }) => {
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isAssociatingContact: true });
    try {
      const response = await CompanyAPI.addContact(companyId, params);
      const association = responsePayload(response);
      commit(types.ADD_COMPANY_ASSOCIATION, {
        type: 'contacts',
        data: association,
      });
      return association;
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
        isAssociatingContact: false,
      });
    }
  },

  removeCompanyContact: async ({ commit }, { companyId, associationId }) => {
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isRemovingAssociation: true });
    try {
      await CompanyAPI.removeContact(companyId, associationId);
      commit(types.DELETE_COMPANY_ASSOCIATION, {
        type: 'contacts',
        id: associationId,
      });
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
        isRemovingAssociation: false,
      });
    }
  },

  fetchRelatedCompanies: async ({ commit }, payload) => {
    const {
      companyId,
      id = companyId,
      ...params
    } = typeof payload === 'object' ? payload : { companyId: payload };
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
      isFetchingRelatedCompanies: true,
    });
    try {
      const response = await CompanyAPI.getRelatedCompanies(id, params);
      const relatedCompanies = responsePayload(response);
      commit(types.SET_COMPANY_ASSOCIATIONS, {
        type: 'relatedCompanies',
        data: relatedCompanies,
      });
      return relatedCompanies;
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
        isFetchingRelatedCompanies: false,
      });
    }
  },

  addRelatedCompany: async ({ commit }, { companyId, ...params }) => {
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isAssociatingCompany: true });
    try {
      const response = await CompanyAPI.addRelatedCompany(companyId, params);
      const relationship = responsePayload(response);
      commit(types.ADD_COMPANY_ASSOCIATION, {
        type: 'relatedCompanies',
        data: relationship,
      });
      return relationship;
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
        isAssociatingCompany: false,
      });
    }
  },

  updateRelatedCompany: async (
    { commit },
    { companyId, relationshipId, ...params }
  ) => {
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
      isUpdatingRelatedCompany: true,
    });
    try {
      const response = await CompanyAPI.updateRelatedCompany(
        companyId,
        relationshipId,
        params
      );
      const relationship = responsePayload(response);
      commit(types.EDIT_COMPANY_ASSOCIATION, {
        type: 'relatedCompanies',
        data: relationship,
      });
      return relationship;
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
        isUpdatingRelatedCompany: false,
      });
    }
  },

  removeRelatedCompany: async ({ commit }, { companyId, relationshipId }) => {
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isRemovingAssociation: true });
    try {
      await CompanyAPI.removeRelatedCompany(companyId, relationshipId);
      commit(types.DELETE_COMPANY_ASSOCIATION, {
        type: 'relatedCompanies',
        id: relationshipId,
      });
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, {
        isRemovingAssociation: false,
      });
    }
  },

  fetchCompanyNotes: async ({ commit }, { companyId, page = 1, ...params }) => {
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isFetchingNotes: true });
    try {
      const response = await CompanyAPI.getNotes(companyId, {
        page,
        per_page: 10,
        ...params,
      });
      commit(types.SET_COMPANY_NOTES, {
        data: response.data,
        page,
      });
      return responsePayload(response);
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isFetchingNotes: false });
    }
  },

  createCompanyNote: async (
    { commit },
    { companyId, content, attachments = [] }
  ) => {
    const trimmedContent = content ? content.trim() : '';
    if (!trimmedContent && !attachments.length) {
      throw new Error('Company note content is required');
    }

    commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isCreatingNote: true });
    try {
      const response = await CompanyAPI.createNote(companyId, {
        content: trimmedContent,
        attachments,
      });
      const note = responsePayload(response);
      commit(types.ADD_COMPANY_NOTE, note);
      return note;
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isCreatingNote: false });
    }
  },

  updateCompanyNote: async (
    { commit },
    { companyId, noteId, content, attachments = [], attachmentsToRemove = [] }
  ) => {
    const trimmedContent = content ? content.trim() : '';
    if (!trimmedContent && !attachments.length) {
      throw new Error('Company note content is required');
    }

    commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isUpdatingNote: true });
    try {
      const response = await CompanyAPI.updateNote(companyId, noteId, {
        content: trimmedContent,
        attachments,
        attachmentsToRemove,
      });
      const note = responsePayload(response);
      commit(types.EDIT_COMPANY_NOTE, note);
      return note;
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isUpdatingNote: false });
    }
  },

  deleteCompanyNote: async ({ commit }, { companyId, noteId }) => {
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isDeletingNote: true });
    try {
      await CompanyAPI.deleteNote(companyId, noteId);
      commit(types.DELETE_COMPANY_NOTE, noteId);
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isDeletingNote: false });
    }
  },

  fetchCompanyTasks: async ({ commit }, { companyId, ...params }) => {
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isFetchingTasks: true });
    try {
      const response = await CompanyAPI.getTasks(companyId, params);
      commit(types.SET_COMPANY_TASKS, response.data);
      return response.data;
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isFetchingTasks: false });
    }
  },

  createCompanyTask: async ({ commit }, { companyId, ...task }) => {
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isCreatingTask: true });
    try {
      const response = await CompanyAPI.createTask(companyId, task);
      commit(types.ADD_COMPANY_TASK, response.data);
      return response.data;
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isCreatingTask: false });
    }
  },

  updateCompanyTask: async ({ commit }, { id, ...task }) => {
    commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isUpdatingTask: true });
    try {
      const response = await CompanyAPI.updateTask(id, task);
      commit(types.EDIT_COMPANY_TASK, response.data);
      return response.data;
    } finally {
      commit(types.SET_COMPANY_PROFILE_UI_FLAG, { isUpdatingTask: false });
    }
  },

  getLabels: async (_, id) => {
    const response = await CompanyAPI.getCompanyLabels(id);
    return responsePayload(response);
  },

  updateLabels: async ({ commit }, { companyId, labels }) => {
    const response = await CompanyAPI.updateCompanyLabels(companyId, labels);
    const labelObjects = responsePayload(response).map(label => ({
      title: label,
    }));
    commit(types.EDIT_COMPANY, { id: companyId, labels: labelObjects });
    return responsePayload(response);
  },

  setSelectedCompanyIds({ commit }, ids) {
    commit(types.SET_SELECTED_COMPANY_IDS, ids);
  },

  clearSelectedCompanyIds({ commit }) {
    commit(types.CLEAR_SELECTED_COMPANY_IDS);
  },

  setCompanyFilters({ commit }, data) {
    commit(types.SET_COMPANY_FILTERS, data);
  },

  clearCompanyFilters({ commit }) {
    commit(types.CLEAR_COMPANY_FILTERS);
  },
};
